import { apiService } from './api';
import { Question, Dimension, ApiResponse } from '../types';

export interface QuestionFilters {
  dimensionId?: number;
  isAiQuestion?: boolean;
  tags?: string[];
  questionnaireLabel?: string;
  questionnaireVersion?: number;
}

export const questionService = {
  async getQuestions(filters: QuestionFilters = {}): Promise<Question[]> {
    const params = new URLSearchParams();
    if (filters.dimensionId) params.append('dimensionId', filters.dimensionId.toString());
    if (filters.isAiQuestion !== undefined) params.append('isAiQuestion', filters.isAiQuestion.toString());
    if (filters.tags?.length) params.append('tags', filters.tags.join(','));
    if (filters.questionnaireLabel) params.append('questionnaireLabel', filters.questionnaireLabel);
    if (filters.questionnaireVersion !== undefined) params.append('questionnaireVersion', filters.questionnaireVersion.toString());
    
    // Add cache-busting parameter
    params.append('_t', Date.now().toString());

    const response = await apiService.get<ApiResponse<{ questions: Question[] }>>(`/questions?${params.toString()}`);
    if (response.data?.questions) {
      return response.data.questions;
    }
    throw new Error(response.message || 'Failed to fetch questions');
  },

  async getQuestion(id: string): Promise<Question> {
    const response = await apiService.get<ApiResponse<{ question: Question }>>(`/questions/${id}`);
    if (response.data?.question) {
      return response.data.question;
    }
    throw new Error(response.message || 'Failed to fetch question');
  },

  async getDimensions(): Promise<Dimension[]> {
    const response = await apiService.get<ApiResponse<{ dimensions: Dimension[] }>>('/dimensions');
    if (response.data?.dimensions) {
      return response.data.dimensions;
    }
    throw new Error(response.message || 'Failed to fetch dimensions');
  },

  async getDimension(id: number): Promise<Dimension> {
    const response = await apiService.get<ApiResponse<{ dimension: Dimension }>>(`/dimensions/${id}`);
    if (response.data?.dimension) {
      return response.data.dimension;
    }
    throw new Error(response.message || 'Failed to fetch dimension');
  },

  async getDimensionQuestions(dimensionId: number): Promise<Question[]> {
    const response = await apiService.get<ApiResponse<{ questions: Question[] }>>(`/dimensions/${dimensionId}/questions`);
    if (response.data?.questions) {
      return response.data.questions;
    }
    throw new Error(response.message || 'Failed to fetch dimension questions');
  },

  async createQuestion(data: Partial<Question>): Promise<Question> {
    const response = await apiService.post<ApiResponse<{ question: Question }>>('/questions', data);
    if (response.data?.question) {
      return response.data.question;
    }
    throw new Error(response.message || 'Failed to create question');
  },

  async updateQuestion(id: string, data: Partial<Question>): Promise<Question> {
    const response = await apiService.put<ApiResponse<{ question: Question }>>(`/questions/${id}`, data);
    if (response.data?.question) {
      return response.data.question;
    }
    throw new Error(response.message || 'Failed to update question');
  },

  async deleteQuestion(id: string): Promise<void> {
    const response = await apiService.delete<ApiResponse<void>>(`/questions/${id}`);
    if (response.message) {
      throw new Error(response.message);
    }
  },
};
